import { createDocValueStore } from './docStore';
import type { OrientAnswer } from './threeDApi';

// WHAT THE ORIENTATION CALL SAID ABOUT ONE MESH, where the viewer can reach it.
//
// The answer is held by the controls under the viewport (MeshControls) and it is needed IN
// the viewport: the six words drawn on the part's bounding box are the app's names for those
// faces, and what the model said about each of them belongs on the same plate — see
// makeExtentDims, which draws it, and OrientViews, which explains it.
//
// The two are siblings on the page with a modal between them (MeshControls is MeshModal's
// `footer`, the viewer is its middle), so the alternative to this was threading a prop from
// the controls up through two components that have no interest in it and back down. A store
// keyed by job id is what every other cross-component answer in this app is.
//
// NOT PERSISTED, deliberately. What is persisted is the EDIT — the turn, its reasoning and
// what the model replied, on the mesh's own history — and this is a view of the answer while
// the mesh is still in the frame the answer was about. See MeshControls, which publishes the
// live answer and the stored one and drops both when a turn is undone: an answer read against
// a mesh that has since moved would put its words on the wrong faces, confidently.
const store = createDocValueStore<OrientAnswer | null>(null);

export const useOrientAnswer = (jobId: number | null | undefined): OrientAnswer | null =>
  store.useValue(jobId == null ? '' : String(jobId));

export const setOrientAnswer = (jobId: number, answer: OrientAnswer | null): void =>
  store.set(String(jobId), answer);

// AND WHETHER THE QUESTION IS OUT RIGHT NOW, which the viewer needs for the same reason and
// cannot get from the answer above: while a call is in flight the answer on hand is the
// PREVIOUS one, read back off the mesh's edit stack, and it stays there until the new one
// lands. Six plates saying which way round the part is, over a button spinning because that
// is exactly what nobody knows — see makeExtentDims, which blanks them on this.
//
// A second store rather than a third state of the first: an answer and a wait are different
// facts, and folding them together would mean every reader of the answer handling a value
// that is not one.
const running = createDocValueStore<boolean>(false);

export const useOrientRunning = (jobId: number | null | undefined): boolean =>
  running.useValue(jobId == null ? '' : String(jobId));

export const setOrientRunning = (jobId: number, on: boolean): void =>
  running.set(String(jobId), on);
